import type { CoachJourneyInput } from '../../schema';
import { PHASE_2, HOOKS_PROMPT, CONTENT_KIND } from './scripts';

/** Posting or scheduling the first video, from the studio or from Copilot, ends the onboarding. */
const POSTED = {
  event: 'post.sent' as const,
  match: ['boardId' as const, 'projectNodeId' as const],
  to: 'finish',
};

/** The second half: the first board, then hooks, a script, filming and posting. */
export const firstBoardSteps: CoachJourneyInput['steps'] = [
  {
    id: 'make-first-board',
    display: 'toast',
    title: 'Making your first board 🧱',
    body: 'One sec…',
    do: [
      {
        action: 'board.create',
        params: { name: 'My first board' },
        bind: { newBoardId: 'firstBoardId' },
      },
      { action: 'board.open', params: { board: 'firstBoardId' }, then: 'enter-first-board' },
    ],
  },
  {
    id: 'enter-first-board',
    display: 'hidden',
    title: 'Opening your first board.',
    body: 'Opening your first board.',
    on: [
      {
        event: 'board.entered',
        matchAs: { boardId: 'firstBoardId' },
        bind: ['boardId'],
        to: 'pick-kind',
      },
    ],
  },
  {
    // The one menu of Phase 2, on the fresh board: what they make decides which Expert Brain
    // lands. Short form and long form are the polished paths; the rest land on the closest
    // framework Poppy has today until each gets its own.
    id: 'pick-kind',
    stage: 'board',
    title: 'What do you want to make first? 🎬',
    body: 'This is your first board: three groups wired to a Poppy chat, just like your brand profile. Your pick decides which Expert Brain goes on it. You can add more later.',
    choices: [
      {
        label: 'Short-form content',
        icons: ['tiktok', 'instagram', 'youtube-shorts'],
        to: 'seed-first-board',
        set: { ...CONTENT_KIND.shortForm },
      },
      {
        label: 'Long-form YouTube',
        icons: ['youtube'],
        to: 'seed-first-board',
        set: { ...CONTENT_KIND.longForm },
      },
      {
        label: 'Meta ads',
        icons: ['meta'],
        to: 'seed-first-board',
        set: { ...CONTENT_KIND.metaAds },
      },
      {
        label: 'Email newsletters',
        icons: ['email'],
        to: 'seed-first-board',
        set: { ...CONTENT_KIND.newsletters },
      },
      { label: 'Copy', icons: ['copy'], to: 'seed-first-board', set: { ...CONTENT_KIND.copy } },
      { label: 'VSL', icons: ['vsl'], to: 'seed-first-board', set: { ...CONTENT_KIND.vsl } },
    ],
  },
  {
    id: 'seed-first-board',
    display: 'toast',
    title: 'Laying out your board 🧠',
    body: 'Expert Brain, Inspiration Content, New Video Ideas, and the prompt.',
    do: [
      {
        action: 'board.add-group',
        params: { groupFrom: 'expertGroup', at: { x: 0, y: 0 } },
        bind: { groupNodeId: 'expertGroupId' },
      },
      // The loadout's shape: the three groups in one column on the left, same width, the
      // chat on the right centred on them, and the prompt as a plain note under the chat.
      {
        action: 'board.add-empty-group',
        params: { title: 'Inspiration Content' },
        bind: { groupNodeId: 'inspirationGroupId' },
      },
      {
        action: 'board.place',
        params: {
          node: 'inspirationGroupId',
          below: 'expertGroupId',
          matchWidth: 'expertGroupId',
          gap: 80,
        },
      },
      {
        action: 'board.add-empty-group',
        params: { title: 'New Video Ideas' },
        bind: { groupNodeId: 'ideasGroupId' },
      },
      {
        action: 'board.place',
        params: {
          node: 'ideasGroupId',
          below: 'inspirationGroupId',
          matchWidth: 'expertGroupId',
          gap: 80,
        },
      },
      {
        // The board's own chat, wired to the three groups; the brand profile is its default
        // voice, so the two brand groups never need to be on this board.
        action: 'board.add-chat',
        params: {
          title: 'Poppy Chat',
          connect: ['expertGroupId', 'inspirationGroupId', 'ideasGroupId'],
        },
        bind: { chatNodeId: 'chatNodeId' },
      },
      {
        action: 'board.place',
        params: {
          node: 'chatNodeId',
          rightOf: 'expertGroupId',
          centerBetween: ['expertGroupId', 'ideasGroupId'],
          gap: 400,
        },
      },
      {
        action: 'board.add-note',
        params: { title: 'Your hooks prompt ✨', text: HOOKS_PROMPT },
        bind: { nodeId: 'promptNoteId' },
      },
      {
        action: 'board.place',
        params: { node: 'promptNoteId', below: 'chatNodeId', matchWidth: 'chatNodeId', gap: 40 },
      },
      {
        action: 'board.show',
        params: { nodes: ['expertGroupId', 'ideasGroupId', 'chatNodeId', 'promptNoteId'] },
      },
      {
        action: 'copilot.ask',
        params: { prompt: "Let's build my first board 🚀", instructions: PHASE_2 },
      },
    ],
    on: [{ event: 'copilot.sent', match: ['boardId'], to: 'building' }],
  },
  {
    // Phase 2 is the conversation too. Poppy signals once the script sounds like them.
    id: 'building',
    // Back on the board after a reload or the start link: Poppy's panel opens again.
    resumeAt: 'reopen-building',
    display: 'hidden',
    keepInView: ['expertGroupId', 'ideasGroupId', 'chatNodeId', 'promptNoteId'],
    framesOnly: true,
    stackGroups: ['inspirationGroupId'],
    title: 'Building your first board with Poppy.',
    body: 'Building your first board with Poppy.',
    on: [
      { event: 'copilot.signal', match: ['boardId'], if: { signal: 'hooks' }, to: 'hooks-fill' },
      { event: 'copilot.signal', match: ['boardId'], if: { signal: 'finished' }, to: 'finish' },
    ],
  },
  {
    // The aha happens in the board's own Poppy Chat: the hooks prompt is typed in for them.
    id: 'hooks-fill',
    display: 'hidden',
    title: 'Typing your hooks prompt.',
    body: 'Typing your hooks prompt.',
    do: {
      action: 'chat.fill',
      params: { chat: 'chatNodeId', prompt: HOOKS_PROMPT },
      then: 'hooks-send',
    },
  },
  {
    id: 'hooks-send',
    title: 'Hit send 🚀',
    body: 'Your hooks prompt is typed into your Poppy Chat. Send it and watch it write 10 hooks in your voice, built from everything on this board.',
    show: [{ anchor: 'chat.input' }],
    focus: { node: 'chatNodeId' },
    resumeAt: 'hooks-fill',
    on: [{ event: 'chat.sent', match: ['boardId', 'chatNodeId'], to: 'hooks-writing' }],
  },
  {
    id: 'hooks-writing',
    display: 'toast',
    title: 'Writing your hooks ✍️',
    body: 'Poppy is reading your board and writing 10 hooks in your voice…',
    resumeAt: 'hooks-pick',
    on: [{ event: 'chat.replied', match: ['boardId', 'chatNodeId'], to: 'hooks-pick' }],
  },
  {
    id: 'hooks-pick',
    title: 'Pick your hook 🎤',
    body: '1. Read the hooks out loud.\n\n2. Click the one that sounds most like you: it drops into the chat box.\n\n3. Hit send, and Poppy turns it into a script.',
    show: [{ anchor: 'chat.input', in: 'chatNodeId', placement: 'right', spotlight: false }],
    on: [
      { event: 'chat.sent', match: ['boardId', 'chatNodeId'], to: 'script-writing' },
      {
        event: 'chat.script-ready',
        match: ['boardId', 'chatNodeId'],
        bind: ['toolCallId'],
        fresh: true,
        to: 'film-now',
      },
      {
        event: 'project.created',
        match: ['boardId'],
        bind: ['projectNodeId'],
        fresh: true,
        to: 'film-record',
      },
    ],
  },
  {
    id: 'script-writing',
    display: 'toast',
    title: 'Writing your script ✍️',
    body: 'Turning your hook into a script in your voice…',
    resumeAt: 'hooks-pick',
    on: [
      {
        event: 'chat.script-ready',
        match: ['boardId', 'chatNodeId'],
        bind: ['toolCallId'],
        fresh: true,
        to: 'film-now',
      },
      {
        event: 'project.created',
        match: ['boardId'],
        bind: ['projectNodeId'],
        fresh: true,
        to: 'film-record',
      },
      // A reply with no script card: back to picking, so they can try again.
      { event: 'chat.replied', match: ['boardId', 'chatNodeId'], to: 'hooks-pick' },
    ],
  },
  {
    id: 'film-now',
    title: 'Your script is ready 🎬',
    body: 'Read it out loud. Sounds like you? Click Add to board to save your script, then open Record to film it.',
    show: [{ anchor: 'chat.script-card.add-to-board' }],
    focus: { node: 'chatNodeId' },
    resumeAt: 'hooks-pick',
    on: [
      {
        event: 'project.created',
        match: ['boardId'],
        bind: ['projectNodeId'],
        fresh: true,
        to: 'film-record',
      },
      { event: 'studio.opened', match: ['boardId'], bind: ['projectNodeId'], to: 'film-record' },
    ],
  },
  {
    id: 'film-record',
    title: 'Film it 📱',
    body: 'Scan the QR code to film on your phone, or record right here on your computer. Your script sits right beside the camera.',
    show: [
      { anchor: 'studio.recording-entry', when: { studio: 'open' } },
      {
        anchor: 'project.open-studio',
        when: { studio: 'closed' },
        title: 'Your script is on your board 🎬',
        body: 'Click Record to open the studio and film it, on your phone or right here.',
      },
    ],
    focus: { node: 'projectNodeId' },
    on: [
      { event: 'studio.camera-chosen', match: ['boardId', 'projectNodeId'], to: 'film-take' },
      { event: 'project.take-saved', match: ['boardId', 'projectNodeId'], to: 'post-ready' },
      POSTED,
    ],
  },
  {
    id: 'film-take',
    display: 'toast',
    title: 'Film your take 🎬',
    body: "Read it like you're talking to a friend. I'll be right here when it saves.",
    show: [
      {
        when: { studio: 'closed' },
        body: 'Open your script to finish filming. I pick up the moment your take saves.',
      },
    ],
    focus: { node: 'projectNodeId' },
    on: [
      { event: 'project.take-saved', match: ['boardId', 'projectNodeId'], to: 'post-ready' },
      POSTED,
    ],
  },
  {
    id: 'post-ready',
    title: 'Your take is in 🔥',
    body: 'Click Review to Post: check your account, the preview and the caption, then post it now or schedule it.',
    show: [
      { anchor: 'studio.post', when: { studio: 'open' } },
      {
        anchor: 'project.open-studio',
        when: { studio: 'closed' },
        title: 'Ready to post 🚀',
        body: 'Open your post to pick your account, preview it and send it out.',
      },
    ],
    focus: { node: 'projectNodeId' },
    on: [{ event: 'post.opened', match: ['boardId', 'projectNodeId'], to: 'post-check' }, POSTED],
  },
  {
    id: 'post-check',
    display: 'inline',
    slot: 'post-dialog',
    title: 'One last look 👀',
    body: 'Pick your account, preview the post and read the caption. Nothing goes out until you choose Post now or Schedule.',
    on: [{ event: 'post.closed', match: ['boardId', 'projectNodeId'], to: 'finish' }, POSTED],
  },
  {
    id: 'finish',
    display: 'hidden',
    title: 'Done.',
    body: 'Done.',
    do: [
      { action: 'app.celebrate' },
      { action: 'app.survey', params: { survey: 'onboarding-tour' }, then: 'end' },
    ],
  },
];
